import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  RefreshCw,
  ArrowLeft,
  Play,
  FileText,
  ChevronDown,
  ArrowUpDown,
} from 'lucide-react';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { payslipsApi } from '../../../api/payslipsApi';
import { payrollRunApi } from '../../../api/payrollRunApi';
import { employeeApi } from '../../../api/employeeApi';
import { toast } from 'react-hot-toast';
import Button from '../../../components/common/Button/Button';
const formatDateDisplay = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDateRange = (start, end) => {
  const startFormatted = formatDateDisplay(start);
  const endFormatted = formatDateDisplay(end);
  if (!startFormatted && !endFormatted) return '';
  return `${startFormatted || 'N/A'} - ${endFormatted || 'N/A'}`;
};

const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const str = value.toString().replace(/,/g, '');
  if (isNaN(str)) return value;

  const parts = str.split('.');
  const formattedInt = new Intl.NumberFormat('en-IN').format(parts[0]);
  return parts.length > 1 ? `${formattedInt}.${parts[1]}` : formattedInt;
};

const formatMonthValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  // Use UTC to avoid local timezone shifts at the end of the month
  // e.g. June 30th 23:59:59Z might be July 1st locally in some timezones
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1;
  return `${year}-${String(month).padStart(2, '0')}`;
};

const EMPLOYEE_PAGE_SIZE = 20;

const GeneratePayslips = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isSubmittingRef = useRef(false);

  const [generateFormData, setGenerateFormData] = useState({
    payGroupId: '',
    payrollMonth: '',
    overrideReason: '',
    approvedBy: 'Admin'
  });

  const [employees, setEmployees] = useState([]);
  const [applyTo, setApplyTo] = useState('Full Salary');
  const [applyValue, setApplyValue] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const {
    data: payGroupOptions = [],
    isLoading: isPayGroupsLoading
  } = useQuery({
    queryKey: ['payrollRuns', 'recent', 10],
    queryFn: async () => {
      const response = await payrollRunApi.getPayrollRuns({ limit: 10 });
      if (response?.responseCode === 200 && Array.isArray(response.data)) {
        const uniqueGroups = [];
        const seen = new Set();
        for (const run of response.data) {
          const rawLabel = run?.payGroup?.groupName || run?.payGroup || run?.groupName || run?.runId;
          if (!rawLabel) continue;
          const range = formatDateRange(
            run?.payPeriodStart || run?.payPeriod?.startDate,
            run?.payPeriodEnd || run?.payPeriod?.endDate
          );
          // Prioritize actual object ID for API calls
          const payGroupValue = run?.id || run?._id || run?.runId || run?.payGroupId;
          const payrollMonthValue = formatMonthValue(run?.payPeriodEnd || run?.payPeriod?.endDate || run?.payPeriodStart || run?.payPeriod?.startDate);
          const finalLabel = normalizedRange => normalizedRange ? `${rawLabel} (${normalizedRange})` : rawLabel;
          const dedupeKey = `${payGroupValue}__${range}`;
          if (seen.has(dedupeKey)) continue;
          seen.add(dedupeKey);
          uniqueGroups.push({
            id: payGroupValue,
            label: finalLabel(range),
            value: payGroupValue,
            period: range,
            payrollMonth: payrollMonthValue
          });
          if (uniqueGroups.length === 10) break;
        }
        return uniqueGroups;
      }
      return [];
    }
  });

  // Fetch employees when payroll run is selected
  const {
    data: runDetails,
    isLoading: isDetailsLoading
  } = useQuery({
    queryKey: ['payrollRunDetails', generateFormData.payGroupId],
    queryFn: () => payrollRunApi.getPayrollRunDetails(generateFormData.payGroupId),
    enabled: !!generateFormData.payGroupId
  });

  // Fetch existing batch items for merging
  const {
    data: existingBatchResponse,
    isLoading: isExistingLoading
  } = useQuery({
    queryKey: ['existingBatch', generateFormData.payGroupId, generateFormData.payrollMonth],
    queryFn: async () => {
      const response = await payslipsApi.getExistingBatch(generateFormData.payGroupId, generateFormData.payrollMonth);
      return response?.data || response;
    },
    enabled: !!generateFormData.payGroupId && !!generateFormData.payrollMonth
  });

  // Update employees state when runDetails or existingBatch is fetched
  useEffect(() => {
    const actualData = runDetails?.data || runDetails;
    const employeesList = actualData?.employees || (Array.isArray(actualData) ? actualData : []);

    // Don't process if employeesList is empty
    if (isDetailsLoading || employeesList.length === 0) return;

    const existingBatch = existingBatchResponse?.data || existingBatchResponse;
    const existingItems = existingBatch?.items || [];

    const mappedEmployees = employeesList.map(emp => {
      const hexId = (emp._id || emp.id)?.toString();
      const customCode = (emp.employeeId || emp.id)?.toString();

      if (!hexId && !customCode) return null;

      // Find if this employee already has a payslip in the existing batch
      const existing = existingItems.find(item => {
        const itemHexId = (item.employee?._id || item.employee)?.toString();
        const itemCustomCode = item.employeeId?.toString();

        // Match by hex ID OR custom employee code
        return (itemHexId && itemHexId === hexId) ||
          (itemCustomCode && itemCustomCode === customCode);
      });

      // Prioritize currentNetPay (pro-rata) as it represents the actual payable amount after attendance
      const baseNetPay = emp.currentNetPay !== undefined ? emp.currentNetPay : (emp.netPay || emp.overallNetPay || 0);

      if (existing) {
        const totalPaidSoFar = existing.totalMonthPay || existing.finalPay || 0;
        const isFullyPaid = totalPaidSoFar >= (baseNetPay || 0);

        return {
          id: customCode || hexId,
          _id: hexId,
          name: emp.name || emp.employeeName || 'N/A',
          netPay: baseNetPay,
          mode: 'Full', // Default to Full for every new batch
          input: '',    // Clear previous inputs
          adjustment: 0, // Reset adjustments
          paidPreviously: totalPaidSoFar,
          isFullyPaid: isFullyPaid,
          selected: !isFullyPaid && emp.status !== 'excluded' // Auto-select only if not fully paid
        };
      }

      return {
        id: customCode || hexId,
        _id: hexId,
        name: emp.name || emp.employeeName || 'N/A',
        netPay: baseNetPay,
        mode: 'Full',
        input: 0,
        adjustment: 0,
        finalPay: baseNetPay, // Default to full net pay for first time
        paidPreviously: 0,
        isFullyPaid: false,
        selected: emp.status !== 'excluded'
      };
    }).filter(Boolean);

    setEmployees(mappedEmployees);
  }, [runDetails, existingBatchResponse, isDetailsLoading]);

  const handlePayGroupChange = (value) => {
    const selected = payGroupOptions.find(group => group.value === value);
    setGenerateFormData(prev => ({
      ...prev,
      payGroupId: value,
      payrollMonth: selected?.payrollMonth || ''
    }));
  };

  const handleApply = () => {
    if (!applyValue && applyTo !== 'Full Salary') return;
    const rawValue = applyValue.toString().replace(/,/g, '');
    const value = parseFloat(rawValue);
    if (isNaN(value) && applyTo !== 'Full Salary') return;

    setEmployees(prev => prev.map(emp => {
      if (!emp.selected) return emp;

      let newMode = emp.mode;
      let newInput = emp.input;
      let newAdjustment = emp.adjustment;

      if (applyTo === 'Full Salary') {
        newMode = 'Full';
        newInput = '';
        // For Full Salary, we don't apply the 'applyValue' to adjustment because the input is hidden
        newAdjustment = emp.adjustment;
      } else if (applyTo === 'Percentage') {
        newMode = 'Percentage';
        const remainingPercent = ((emp.netPay - emp.paidPreviously) / emp.netPay) * 100;
        newInput = Math.min(value, remainingPercent);
      } else if (applyTo === 'Fixed Amount') {
        newMode = 'Fixed';
        const remaining = emp.netPay - emp.paidPreviously;
        newInput = Math.min(value, remaining);
      }

      const updatedEmp = { ...emp, mode: newMode, input: newInput, adjustment: newAdjustment };
      const finalPayAmount = parseFloat(calculateFinalPay(updatedEmp));

      return {
        ...updatedEmp,
        selected: emp.selected && finalPayAmount > 0 // Auto-unselect if nothing to pay
      };
    }));
  };

  const toggleSelectAll = () => {
    const allSelected = employees.every(emp => emp.selected);
    setEmployees(prev => prev.map(emp => ({ ...emp, selected: !allSelected })));
  };

  const toggleEmployeeSelect = (id) => {
    setEmployees(prev => prev.map(emp =>
      emp.id === id ? { ...emp, selected: !emp.selected } : emp
    ));
  };

  const handleEmployeeChange = (id, field, value) => {
    // Strip commas for internal handling and validation
    const rawValue = value.toString().replace(/,/g, '');

    // Only allow numbers and decimal point for numeric fields
    if ((field === 'input' || field === 'adjustment') && rawValue !== '' && !/^\d*\.?\d*$/.test(rawValue)) return;

    setEmployees(prev => prev.map(emp => {
      if (emp.id !== id) return emp;

      let finalValue = rawValue;

      // Handle mode change separately to set defaults
      if (field === 'mode') {
        return {
          ...emp,
          mode: value,
          input: value === 'Full' ? '' : emp.input
        };
      }

      // Logic to prevent overpayment during entry
      const remaining = Math.max(0, emp.netPay - emp.paidPreviously);
      if (field === 'input') {
        const numValue = parseFloat(rawValue);
        if (!isNaN(numValue)) {
          if (emp.mode === 'Percentage') {
            const maxPercent = (remaining / emp.netPay) * 100;
            if (numValue > maxPercent) finalValue = maxPercent.toFixed(2);
          } else if (emp.mode === 'Fixed') {
            if (numValue > remaining) finalValue = remaining.toFixed(2);
          }
        }
      }
      if (field === 'adjustment') {
        const numValue = parseFloat(rawValue);
        if (!isNaN(numValue) && numValue > remaining) {
          finalValue = remaining.toFixed(2);
        }
      }

      const updatedEmp = { ...emp, [field]: finalValue };
      const finalPayAmount = parseFloat(calculateFinalPay(updatedEmp));

      return {
        ...updatedEmp,
        selected: emp.selected && finalPayAmount > 0 // Keep unselected if nothing to pay
      };
    }));
  };

  const calculateBatchPayable = (emp) => {
    const base = parseFloat(emp.netPay || 0);
    const input = parseFloat(emp.input || 0);
    const remaining = Math.max(0, base - (emp.paidPreviously || 0));

    if (emp.mode === 'Full') {
      return remaining;
    } else if (emp.mode === 'Percentage') {
      const amount = (base * (input / 100));
      return Math.min(amount, remaining); // Cap at remaining balance
    } else if (emp.mode === 'Fixed') {
      return Math.min(input, remaining); // Cap at remaining balance
    }
    return 0;
  };

  const calculateFinalPay = (emp) => {
    const batchPayable = calculateBatchPayable(emp);
    const adjustment = parseFloat(emp.adjustment || 0);
    const remaining = Math.max(0, emp.netPay - (emp.paidPreviously || 0));

    // Total pay for this batch cannot exceed remaining balance
    const totalBatch = batchPayable + adjustment;
    return Math.max(0, Math.min(totalBatch, remaining)).toFixed(2);
  };

  const calculateHold = (emp) => {
    const totalMonthNet = parseFloat(emp.proRataNetPay || emp.netPay || 0);
    const finalPay = parseFloat(calculateFinalPay(emp));
    const cumulativePaid = (emp.paidPreviously || 0) + finalPay;
    const hold = totalMonthNet - cumulativePaid;
    return Math.max(0, hold).toFixed(2);
  };

  const totalNetPay = employees.reduce((sum, emp) => sum + (emp.selected ? emp.netPay : 0), 0);
  const totalPaidPreviously = employees.reduce((sum, emp) => sum + (emp.selected ? emp.paidPreviously : 0), 0);
  const totalFinalPay = employees.reduce((sum, emp) => sum + (emp.selected ? parseFloat(calculateFinalPay(emp)) : 0), 0);
  const totalHold = employees.reduce((sum, emp) => sum + (emp.selected ? parseFloat(calculateHold(emp)) : 0), 0);

  const sortedEmployees = useMemo(() => {
    let sortableItems = [...employees];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle calculated fields
        if (sortConfig.key === 'finalPay') {
          aVal = parseFloat(calculateFinalPay(a));
          bVal = parseFloat(calculateFinalPay(b));
        } else if (sortConfig.key === 'holdAmount') {
          aVal = parseFloat(calculateHold(a));
          bVal = parseFloat(calculateHold(b));
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [employees, sortConfig]);

  const generatePayslipsMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await payslipsApi.generatePayslips(payload);
      return response;
    },
    onSuccess: (response) => {
      isSubmittingRef.current = false;
      toast.success(`Generated payslips successfully`);
      navigate('/payslips');
    },
    onError: (error) => {
      toast.error(error?.message || 'Failed to generate payslips');
      isSubmittingRef.current = false;
    }
  });

  const handleSubmitGenerate = () => {
    if (!generateFormData.payGroupId) {
      toast.error('Please select a pay group.');
      return;
    }

    const selectedEmployees = employees.filter(e => e.selected);
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee.');
      return;
    }

    // Identify who actually needs a top-up payment
    const employeesToProcess = selectedEmployees.filter(e => {
      const deltaPay = parseFloat(calculateFinalPay(e));
      return deltaPay > 0;
    });

    // Case: No one in the selection needs payment
    if (employeesToProcess.length === 0) {
      toast.error('The selected employees have already been paid the target amount or more for this month.');
      return;
    }

    // Case: Partial selection needs payment
    if (employeesToProcess.length < selectedEmployees.length) {
      const skippedCount = selectedEmployees.length - employeesToProcess.length;
      toast(`Processing ${employeesToProcess.length} employees. (${skippedCount} skipped as they are already fully paid for the current target)`, {
        duration: 5000,
        icon: 'ℹ️'
      });
    }

    isSubmittingRef.current = true;
    generatePayslipsMutation.mutate({
      payGroupId: generateFormData.payGroupId,
      payrollMonth: generateFormData.payrollMonth,
      employees: employeesToProcess.map(e => ({
        employeeId: e.id,
        finalPay: calculateFinalPay(e),
        mode: e.mode,
        input: e.input,
        adjustment: e.adjustment,
        holdAmount: calculateHold(e)
      }))
    });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Premium Header Layout */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Run Payslip</h1>
          <p className="text-sm text-gray-500 mt-1">Distribute and manage employee salary payments for this month</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/payslips')}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-all shadow-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmitGenerate}
            disabled={generatePayslipsMutation.isPending || !generateFormData.payGroupId}
            className="flex items-center gap-2 px-5 py-2 bg-[#0F2942] hover:bg-[#0B2033] text-white rounded-lg text-sm font-bold transition-all shadow-md active:scale-95 disabled:opacity-50 disabled:active:scale-100"
          >
            <Play className="w-4 h-4 fill-current" />
            {generatePayslipsMutation.isPending ? 'Processing...' : 'Run Payslip'}
          </button>
        </div>
      </div>

      {/* Unified Filter Bar Row */}
      <div className="bg-white p-2 rounded-xl shadow-sm border border-gray-200 mb-6 flex flex-wrap items-center gap-4">

        <Button
          variant="outline"
          icon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>

        {/* Pay Group Select - Stylized as Search */}
        <div className="flex-1 min-w-[300px] relative">
          <select
            value={generateFormData.payGroupId}
            onChange={(e) => handlePayGroupChange(e.target.value)}
            className="w-full pl-4 pr-10 py-2 bg-white border border-gray-200 hover:border-gray-300 focus:bg-white focus:border-blue-500 rounded-lg text-sm font-bold text-gray-800 outline-none transition-all appearance-none cursor-pointer shadow-sm"
          >
            <option value="">Select Pay Group for this month...</option>
            {payGroupOptions.map((group) => (
              <option key={group.value} value={group.value}>
                {group.label}
              </option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            <ChevronDown className="w-4 h-4" />
          </div>
        </div>

        <div className="h-8 w-px bg-gray-200 hidden md:block"></div>

        {/* Apply To Tools */}
        <div className="flex items-center gap-3 pr-2">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Apply to:</span>
          <select
            value={applyTo}
            onChange={(e) => setApplyTo(e.target.value)}
            className="px-3 py-1.5 border border-gray-200 bg-white rounded-lg text-sm font-bold text-gray-800 focus:ring-2 focus:ring-[#0F2942]/50/20 focus:border-blue-500 outline-none cursor-pointer transition-all shadow-sm"
          >
            <option value="Full Salary">Full Salary</option>
            <option value="Percentage">Percentage (%)</option>
            <option value="Fixed Amount">Fixed Amount (₹)</option>
          </select>

          {applyTo !== 'Full Salary' && (
            <input
              type="text"
              placeholder={applyTo === 'Percentage' ? "e.g. 50" : "e.g. 1000"}
              onChange={(e) => {
                let val = e.target.value.replace(/,/g, '');
                if (val !== '' && !/^\d*\.?\d*$/.test(val)) return;
                if (applyTo === 'Percentage' && parseFloat(val) > 100) val = "100";
                setApplyValue(val);
              }}
              value={formatNumberWithCommas(applyValue)}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm w-44 focus:ring-2 focus:ring-[#0F2942]/50/20 outline-none font-bold text-[#0F2942]"
            />
          )}

          <button
            onClick={handleApply}
            className="px-4 py-1.5 bg-gray-100 hover:bg-[#0F2942] hover:text-white text-gray-700 rounded-lg text-xs font-bold transition-all"
          >
            Apply All
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className="px-3 py-1 bg-blue-100 text-[#0B2033] rounded-full text-xs font-bold uppercase tracking-wide">
          {generateFormData.payrollMonth ? new Date(generateFormData.payrollMonth).toLocaleString('default', { month: 'long', year: 'numeric' }) : 'Pending Month'}
        </div>
        <div className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-bold uppercase tracking-wide">
          {employees.filter(e => e.selected).length} Selected
        </div>
      </div>

      {/* Employees Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-xl shadow-sm bg-white">
        <table className="w-full text-left border-collapse">
          <thead className="bg-blue-50/50 text-gray-900 text-xs font-semibold uppercase tracking-wider border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-center w-16">
                <input
                  type="checkbox"
                  checked={employees.length > 0 && employees.every(emp => emp.selected)}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 text-[#0F2942] border-gray-300 rounded focus:ring-[#0F2942]/50/20 cursor-pointer transition-all"
                />
              </th>
              <th className="px-6 py-4">
                <div
                  className="flex items-center gap-1 cursor-pointer hover:text-[#0F2942] transition-colors"
                  onClick={() => handleSort('name')}
                >
                  Employee
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th className="px-6 py-4">
                <div
                  className="flex items-center gap-1 cursor-pointer hover:text-[#0F2942] transition-colors"
                  onClick={() => handleSort('mode')}
                >
                  Mode
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th className="px-6 py-4">Input</th>
              <th className="px-6 py-4 text-right">
                <div
                  className="flex items-center justify-end gap-1 cursor-pointer hover:text-[#0F2942] transition-colors"
                  onClick={() => handleSort('netPay')}
                >
                  Net Pay
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th className="px-6 py-4 text-right">
                <div
                  className="flex items-center justify-end gap-1 cursor-pointer hover:text-[#0F2942] transition-colors"
                  onClick={() => handleSort('paidPreviously')}
                >
                  Paid
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th className="px-6 py-4 text-right">Adjustment</th>
              <th className="px-6 py-4 text-right text-[#0F2942] font-bold">
                <div
                  className="flex items-center justify-end gap-1 cursor-pointer hover:text-blue-800 transition-colors"
                  onClick={() => handleSort('finalPay')}
                >
                  Final Pay
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th className="px-6 py-4 text-right text-amber-600 font-bold">
                <div
                  className="flex items-center justify-end gap-1 cursor-pointer hover:text-amber-800 transition-colors"
                  onClick={() => handleSort('holdAmount')}
                >
                  Remaining/Hold
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isDetailsLoading || isExistingLoading ? (
              <tr>
                <td colSpan="9" className="px-6 py-12 text-center text-gray-500 italic">
                  <div className="flex items-center justify-center gap-2">
                    <RefreshCw className="h-5 w-5 animate-spin" />
                    Loading payroll data...
                  </div>
                </td>
              </tr>
            ) : sortedEmployees.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-6 py-12 text-center text-gray-400 italic font-medium">
                  Select a pay group to view employees.
                </td>
              </tr>
            ) : (
              sortedEmployees.map((emp) => (
                <tr key={emp.id} className={`hover:bg-blue-50/40 transition-colors border-b border-gray-100 last:border-0 ${emp.isFullyPaid ? 'bg-green-50/20' : ''}`}>
                  <td className="px-6 py-4 text-center">
                    <input
                      type="checkbox"
                      checked={emp.selected}
                      disabled={emp.isFullyPaid}
                      onChange={() => toggleEmployeeSelect(emp.id)}
                      className="w-4 h-4 text-[#0F2942] border-gray-300 rounded focus:ring-[#0F2942]/50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-[#0F2942] flex-shrink-0">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div className="flex items-center gap-1 flex-wrap">
                        <div className="text-sm font-bold text-gray-900 whitespace-nowrap">{emp.name}</div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase bg-gray-100 px-1.5 py-0.1 rounded">ID: {emp.id}</span>
                        {emp.isFullyPaid && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-green-100 text-green-700 uppercase">Fully Paid</span>
                        )}
                        {emp.paidPreviously > 0 && !emp.isFullyPaid && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-blue-100 text-[#0B2033] uppercase">Partially Paid</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={emp.mode}
                      disabled={emp.isFullyPaid}
                      onChange={(e) => handleEmployeeChange(emp.id, 'mode', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-md text-xs outline-none focus:ring-1 focus:ring-[#0F2942]/50 font-medium disabled:bg-gray-50 disabled:text-gray-400"
                    >
                      <option value="Full">Full</option>
                      <option value="Percentage">Percentage</option>
                      <option value="Fixed">Fixed</option>
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    {emp.mode !== 'Full' ? (
                      <div className="flex flex-col">
                        <input
                          type="text"
                          value={formatNumberWithCommas(emp.input)}
                          disabled={emp.isFullyPaid}
                          placeholder={emp.mode === 'Percentage' ? "%" : "Amount"}
                          onChange={(e) => handleEmployeeChange(emp.id, 'input', e.target.value.replace(/,/g, ''))}
                          className="px-2 py-1.5 border border-gray-300 rounded-md text-xs w-full min-w-[140px] max-w-[160px] outline-none focus:ring-1 focus:ring-[#0F2942]/50 font-bold text-[#0F2942] disabled:bg-gray-50 disabled:text-gray-400"
                        />
                        <span className="text-[9px] text-gray-400 mt-1 font-medium">
                          {emp.mode === 'Percentage'
                            ? `Max: ${(((emp.netPay - emp.paidPreviously) / emp.netPay) * 100).toFixed(1)}%`
                            : `Max: ₹${formatNumberWithCommas((emp.netPay - emp.paidPreviously).toFixed(2))}`}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400 italic text-[10px]">{emp.isFullyPaid ? 'Released' : 'N/A (Full Pay)'}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right text-gray-700 font-mono text-sm">₹{emp.netPay.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td className="px-6 py-4 text-right text-[#0B2033] font-mono text-sm font-bold">₹{emp.paidPreviously.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td className="px-6 py-4 text-right">
                    <input
                      type="text"
                      value={formatNumberWithCommas(emp.adjustment)}
                      disabled={emp.isFullyPaid}
                      onChange={(e) => handleEmployeeChange(emp.id, 'adjustment', e.target.value)}
                      className="px-2 py-1.5 border border-gray-300 rounded-md text-xs w-full min-w-[120px] max-w-[140px] text-right outline-none focus:ring-1 focus:ring-[#0F2942]/50 font-medium disabled:bg-gray-50 disabled:text-gray-400"
                      placeholder="0.00"
                    />
                  </td>
                  <td className="px-6 py-4 text-right font-bold text-[#0B2033] font-mono text-sm">
                    ₹{parseFloat(calculateFinalPay(emp)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-6 py-4 text-right font-bold text-amber-600 font-mono text-sm">
                    ₹{parseFloat(calculateHold(emp)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Totals Summary */}
      {employees.length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-blue-50/30 p-4 rounded-xl border border-blue-100/50">
            <p className="text-[10px] text-[#0F2942] font-bold uppercase tracking-wider mb-1">Total Net Pay</p>
            <p className="text-xl font-bold text-blue-900">₹{totalNetPay.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="bg-gray-50/30 p-4 rounded-xl border border-gray-100/50">
            <p className="text-[10px] text-gray-600 font-bold uppercase tracking-wider mb-1">Total Prev. Paid</p>
            <p className="text-xl font-bold text-gray-900">₹{totalPaidPreviously.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="bg-green-50/30 p-4 rounded-xl border border-green-100/50">
            <p className="text-[10px] text-green-600 font-bold uppercase tracking-wider mb-1">Final Pay (This Batch)</p>
            <p className="text-xl font-bold text-green-900">₹{totalFinalPay.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="bg-amber-50/30 p-4 rounded-xl border border-amber-100/50">
            <p className="text-[10px] text-amber-600 font-bold uppercase tracking-wider mb-1">Total Hold Amount</p>
            <p className="text-xl font-bold text-amber-900">₹{totalHold.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneratePayslips;
